import { ChipType } from 'components/@extended/StatusChip';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import React from 'react';
import { ENTITY_LINK_CLASS } from 'pages/matters/util';
import { ClassifierData } from 'types/classifiers';
import { Contract, ContractListItem, ContractParty, ContractStatusType, ContractType } from 'types/contracts';
import { getClassifierText, t } from 'utils/getTranslations';
import { Office } from 'types/offices';

export const getContractParties = (listItems: ContractParty[]): React.ReactNode[] => {
    const links: React.ReactNode[] = [];
    const parties = (listItems || [])
        .toSorted((a, b) => a.party.name.localeCompare(b.party.name))
        .map((item) => item.party);

    parties.forEach((item, index) => {
        const optionalComma = index < listItems.length - 1 ? `,\u00A0` : null;
        links.push(
            <React.Fragment key={item.id}>
                <PartyName party={item} isLink />
                {optionalComma}
            </React.Fragment>
        );
    });
    return links;
};

const mapOffices = (offices: Office[] | undefined): React.ReactNode[] => {
    const links: React.ReactNode[] = [];

    (offices || []).forEach((item, index) => {
        const optionalComma = index < (offices || []).length - 1 ? `,\u00A0` : null;

        links.push(
            <React.Fragment key={item.id}>
                <RecordLink className={ENTITY_LINK_CLASS} to={`/office/${item.id}`} label={item.name} />
                {optionalComma}
            </React.Fragment>
        );
    });

    return links;
};

export const getAppliedInOffice = (contract: Contract | ContractListItem | undefined): React.ReactNode => {
    if (!contract) return null;

    const { tenantCompany, companies } = contract;
    const companiesList = companies?.map((item) => item.company) || [];
    if (tenantCompany && !companiesList.some((val) => val.id === tenantCompany.id)) companiesList.push(tenantCompany);

    const offices: Office[] = companiesList
        .map((c) => c.offices || [])
        .reduce((prev: Office[], cur: Office[]) => prev.concat(cur), [])
        .toSorted((a, b) => a.name.localeCompare(b.name));

    return mapOffices(offices);
};

export const getAppliedInOfficeContent = (
    contract: Contract | undefined,
    allTenantCompaniesCount?: number
): React.ReactNode => {
    if (!contract) return null;

    const { tenantCompany, companies } = contract;
    const companiesList = companies?.map((item) => item.company) || [];
    if (tenantCompany && !companiesList.some((val) => val.id === tenantCompany.id)) companiesList.push(tenantCompany);

    return allTenantCompaniesCount === 1 ? (
        <div>[{mapOffices(tenantCompany?.offices)}]</div>
    ) : (
        <>
            {companiesList
                .toSorted((a, b) => a.name.localeCompare(b.name))
                .map((company) => (
                    <div key={company.id}>
                        {company.name} [{mapOffices(company.offices)}]
                    </div>
                ))}
        </>
    );
};

/** A client identification is received while its contract is valid, and superseded once a newer one terminates it. */
const CLIENT_IDENTIFICATION_STATUS: Partial<Record<string, string>> = {
    [ContractStatusType.VALID]: 'client-identification.status.RECEIVED',
    [ContractStatusType.TERMINATED]: 'client-identification.status.SUPERSEDED',
    [ContractStatusType.DRAFT]: 'client-identification.status.DRAFT',
};

/** A contract's status as a list shows it: a client identification by its own words, any other by the classifier. */
export function contractStatusLabel(
    contract: Pick<ContractListItem, 'contractTypeCode' | 'contractStatusTypeCode'>,
    classifiers: Record<string, ClassifierData>
): string {
    const ownId =
        contract.contractTypeCode === ContractType.CLIENT_IDENTIFICATION && contract.contractStatusTypeCode
            ? CLIENT_IDENTIFICATION_STATUS[contract.contractStatusTypeCode]
            : undefined;
    return ownId ? t(ownId) : getClassifierText(classifiers['CONTRACT_STATUS_TYPE'], contract.contractStatusTypeCode);
}

export const getContractStatusChipType = (contractStatusTypeCode: string | null | undefined): ChipType => {
    switch (contractStatusTypeCode) {
        case ContractStatusType.VALID:
            return 'success';
        case ContractStatusType.DRAFT:
            return 'outlined';
        default:
            return 'inactive';
    }
};
